import { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { vendorsApi } from '../lib/api';
import type { VendorRegistryItem } from '../types';
import AppShell from '../components/AppShell';
import { useTenantContext } from '../context/TenantContext';

export default function VendorRegistryPage() {
  const { user, loading } = useAuth();
  const [vendors, setVendors] = useState<VendorRegistryItem[]>([]);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const isSuperAdmin = user?.role === 'super_admin';
  const { selectedTenantId } = useTenantContext();

  useEffect(() => {
    if (!user) return;
    if (isSuperAdmin && selectedTenantId === undefined) return;
    setFetching(true);
    setExpanded(null);
    vendorsApi
      .registry(isSuperAdmin ? selectedTenantId : undefined)
      .then(setVendors)
      .catch((e) => setError(e.message))
      .finally(() => setFetching(false));
  }, [user, isSuperAdmin, selectedTenantId]);

  if (loading || !user) return null;

  const benchmarked = vendors.filter((v) => v.benchmark !== null).length;
  const hasBenchmarks = benchmarked > 0;

  const subtitle = fetching
    ? undefined
    : `${vendors.length} vendor${vendors.length !== 1 ? 's' : ''} · ${hasBenchmarks ? `${benchmarked} benchmarked` : 'No benchmarks run yet'}`;

  const actions = <RenewalLegend />;

  return (
    <AppShell user={user} title="Vendor Registry" subtitle={subtitle} actions={actions}>
      {error && <ErrorBanner message={error} />}

      {fetching ? (
        <p style={{ color: '#94a3b8', fontSize: '0.9rem' }}>Loading…</p>
      ) : vendors.length === 0 ? (
        <EmptyState />
      ) : (
        <div style={{
          background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 10,
          overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        }}>
          <div style={tableHeader}>
            <span style={{ flex: '2' }}>Vendor / Product</span>
            <span style={{ flex: '1.2', textAlign: 'right' }}>Annual Spend</span>
            <span style={{ flex: '1', textAlign: 'center' }}>Seats</span>
            <span style={{ flex: '1.2', textAlign: 'center' }}>Renewal</span>
            {hasBenchmarks && <>
              <span style={{ flex: '1.2', textAlign: 'right' }}>Market Median</span>
              <span style={{ flex: '1', textAlign: 'center' }}>Overpayment</span>
              <span style={{ flex: '1', textAlign: 'center' }}>Status</span>
            </>}
          </div>
          {vendors.map((v) => (
            <VendorRow
              key={v.id}
              vendor={v}
              showBenchmark={hasBenchmarks}
              expanded={expanded === v.id}
              onToggle={() => setExpanded(expanded === v.id ? null : v.id)}
            />
          ))}
        </div>
      )}
    </AppShell>
  );
}

function VendorRow({
  vendor: v, showBenchmark, expanded, onToggle,
}: {
  vendor: VendorRegistryItem; showBenchmark: boolean; expanded: boolean; onToggle: () => void;
}) {
  const b = v.benchmark;
  const overpayment = b?.overpaymentPct ?? null;
  const trafficLight = overpayment == null ? null : overpayment > 25 ? 'red' : overpayment > 10 ? 'amber' : 'green';
  const renewalColor = renewalBadgeColor(v.daysUntilRenewal);

  return (
    <div style={{ borderTop: '1px solid #e2e8f0' }}>
      <div
        onClick={b ? onToggle : undefined}
        style={{
          ...tableRow,
          cursor: b ? 'pointer' : 'default',
          backgroundColor: expanded ? '#f8fafc' : '#ffffff',
        }}
        onMouseEnter={(e) => { if (!expanded) e.currentTarget.style.backgroundColor = '#f8fafc'; }}
        onMouseLeave={(e) => { if (!expanded) e.currentTarget.style.backgroundColor = '#ffffff'; }}
      >
        <div style={{ flex: '2', display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '0.9rem', fontWeight: 400, color: '#0f172a' }}>{v.name}</span>
            <CategoryBadge category={v.category} />
          </div>
          {v.product && <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{v.product}</span>}
        </div>
        <div style={{ flex: '1.2', textAlign: 'right' }}>
          <span style={{ fontSize: '0.9rem', color: '#0f172a', fontWeight: 400 }}>
            {v.annualContractValue != null ? formatCurrency(v.annualContractValue) : '—'}
          </span>
        </div>
        <div style={{ flex: '1', textAlign: 'center' }}>
          <span style={{ fontSize: '0.85rem', color: '#94a3b8' }}>
            {v.currentVolume != null
              ? `${v.currentVolume.toLocaleString()} ${v.unit ?? ''}`
              : v.unit === 'ACV' ? 'ACV-based' : '—'}
          </span>
        </div>
        <div style={{ flex: '1.2', textAlign: 'center' }}>
          {v.renewalDate ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.15rem' }}>
              <span style={{ fontSize: '0.78rem', color: renewalColor, fontWeight: 400 }}>
                {formatDate(v.renewalDate)}
              </span>
              {v.daysUntilRenewal != null && v.daysUntilRenewal >= 0 && (
                <span style={{ fontSize: '0.7rem', color: renewalColor }}>
                  {v.daysUntilRenewal === 0 ? 'Today' : `${v.daysUntilRenewal}d`}
                </span>
              )}
            </div>
          ) : (
            <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>—</span>
          )}
        </div>
        {showBenchmark && (
          <>
            <div style={{ flex: '1.2', textAlign: 'right' }}>
              {b?.marketMedianAtVolume != null ? (
                <span style={{ fontSize: '0.85rem', color: '#94a3b8' }}>
                  {formatCurrencyUnit(b.adjustedMarketMedian ?? b.marketMedianAtVolume, v.unit)}
                </span>
              ) : (
                <span style={{ color: '#cbd5e1', fontSize: '0.8rem' }}>–</span>
              )}
            </div>
            <div style={{ flex: '1', textAlign: 'center' }}>
              {overpayment != null ? (
                <span style={{
                  fontSize: '0.82rem', fontWeight: 400,
                  color: trafficLight === 'red' ? '#ef4444' : trafficLight === 'amber' ? '#f59e0b' : '#10b981',
                }}>
                  {overpayment > 0 ? `+${overpayment.toFixed(0)}%` : `${overpayment.toFixed(0)}%`}
                </span>
              ) : (
                <span style={{ color: '#cbd5e1', fontSize: '0.8rem' }}>–</span>
              )}
            </div>
            <div style={{ flex: '1', textAlign: 'center' }}>
              {trafficLight ? <TrafficLight status={trafficLight} /> : <span style={{ color: '#cbd5e1', fontSize: '0.8rem' }}>No data</span>}
            </div>
          </>
        )}
      </div>

      {expanded && b && (
        <div style={{
          backgroundColor: '#f8fafc', borderTop: '1px solid #e2e8f0',
          padding: '1.25rem 1.5rem',
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.25rem',
        }}>
          <DetailGroup label="Volume Tier" value={b.volumeTier ?? '—'} />
          <DetailGroup label="List Price" value={b.listPriceAtVolume != null ? formatCurrencyUnit(b.listPriceAtVolume, v.unit) : '—'} />
          <DetailGroup label="Market Median" value={b.marketMedianAtVolume != null ? formatCurrencyUnit(b.marketMedianAtVolume, v.unit) : '—'} />
          <DetailGroup label="Best Negotiated" value={b.bestNegotiatedAtVolume != null ? formatCurrencyUnit(b.bestNegotiatedAtVolume, v.unit) : '—'} />
          <DetailGroup
            label="Savings Potential"
            value={b.adjustedSavingsUsd != null
              ? formatCurrency(b.adjustedSavingsUsd) + '/yr (adjusted)'
              : b.savingsPotentialUsd != null
                ? formatCurrency(b.savingsPotentialUsd) + '/yr'
                : '—'}
            accent
          />
          <DetailGroup label="Contract" value={[v.contractType, v.contractLengthMonths ? `${v.contractLengthMonths}mo` : null, v.paymentTerms].filter(Boolean).join(' · ') || '—'} />
          {b.negotiationLevers.length > 0 && (
            <div style={{ gridColumn: '1 / -1' }}>
              <p style={{ fontSize: '0.7rem', fontWeight: 500, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 0.5rem' }}>
                Negotiation Levers
              </p>
              <ul style={{ margin: 0, paddingLeft: '1.1rem' }}>
                {b.negotiationLevers.map((lever, i) => (
                  <li key={i} style={{ fontSize: '0.8rem', color: '#475569', lineHeight: 1.6 }}>{lever}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TrafficLight({ status }: { status: 'red' | 'amber' | 'green' }) {
  const colors = { red: '#ef4444', amber: '#f59e0b', green: '#10b981' };
  const labels = { red: 'Over-market', amber: 'Above median', green: 'At market' };
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}>
      <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: colors[status], flexShrink: 0 }} />
      <span style={{ fontSize: '0.72rem', color: colors[status], fontWeight: 400 }}>{labels[status]}</span>
    </div>
  );
}

function CategoryBadge({ category }: { category: string }) {
  return (
    <span style={{
      fontSize: '0.65rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em',
      backgroundColor: '#e0f2fe', color: '#0369a1',
      padding: '0.1rem 0.45rem', borderRadius: 4,
    }}>
      {category.replace('_', ' ')}
    </span>
  );
}

function DetailGroup({ label, value, accent = false }: { label: string; value: string; accent?: boolean }) {
  return (
    <div>
      <p style={{ fontSize: '0.68rem', fontWeight: 500, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 0.25rem' }}>{label}</p>
      <p style={{ fontSize: '0.88rem', color: accent ? '#10b981' : '#0f172a', fontWeight: accent ? 400 : 300, margin: 0 }}>{value}</p>
    </div>
  );
}

function RenewalLegend() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', fontSize: '0.72rem', color: '#94a3b8' }}>
      <span>Renewal:</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
        <span style={{ color: '#ef4444' }}>■</span> &lt;90 days
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
        <span style={{ color: '#f59e0b' }}>■</span> 91–180 days
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div style={{
      backgroundColor: '#ffffff', border: '1px solid #e2e8f0',
      borderRadius: 10, padding: '3rem 2rem', textAlign: 'center',
    }}>
      <p style={{ color: '#64748b', fontSize: '0.9rem', margin: 0 }}>
        No vendors found. Vendors are added during onboarding by your SpendLens administrator.
      </p>
    </div>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div style={{
      backgroundColor: '#fee2e2', border: '1px solid rgba(239,68,68,0.3)',
      borderRadius: 8, padding: '0.75rem 1rem', color: '#991b1b',
      fontSize: '0.85rem', marginBottom: '1.25rem',
    }}>
      {message}
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────
function formatCurrency(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

function formatCurrencyUnit(n: number, unit: string | null): string {
  return `$${n.toFixed(2)}${unit ? `/${unit}` : ''}`;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function renewalBadgeColor(days: number | null): string {
  if (days == null) return '#94a3b8';
  if (days < 0) return '#cbd5e1';
  if (days <= 90) return '#ef4444';
  if (days <= 180) return '#f59e0b';
  return '#94a3b8';
}

// ── Styles ─────────────────────────────────────────────────────────
const tableHeader: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: '1rem',
  padding: '0.6rem 1.25rem',
  backgroundColor: '#f8fafc',
  borderBottom: '1px solid #e2e8f0',
  fontSize: '0.68rem', fontWeight: 500, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em',
};

const tableRow: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: '1rem',
  padding: '0.85rem 1.25rem', transition: 'background-color 0.1s',
};
